/* ═══════════════════════════════════════════════════════════════
   NOVA — LUX KIT (Microinteractions Controller)
   Magnetic proximity + card tilt via pointer tracking
   
   FEATURES:
   • Magnetic proximity (buttons)
   • Card tilt/parallax (3D pointer response)
   • rAF-throttled pointer tracking
   • Zero cost unless enabled
   
   GATING:
   • Theme setting: body[data-nova-cosmic-interactions]
   • localStorage kill switch: nova_lux_off
   • prefers-reduced-motion: reduce
   • (hover:hover) AND (pointer:fine)
   
   PERFORMANCE:
   • One delegated pointermove listener
   • rAF batching (60fps max)
   • getBoundingClientRect cached on enter
   • AbortController cleanup
   • Theme Editor safe
   
   © 2025 Nova's Gems — Iteration 5
   ═══════════════════════════════════════════════════════════════ */

(() => {
  "use strict";

  const VERSION = "1.0.0";
  const STORE_KEY = "nova_lux_off";
  const SELECT_MAGNET = '[data-nova-magnetic="1"]';
  const SELECT_TILT = '[data-nova-tilt="1"]';

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // GATING CHECKS
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const mqFine = () => window.matchMedia("(hover: hover) and (pointer: fine)");
  const mqReduce = () => window.matchMedia("(prefers-reduced-motion: reduce)");

  function isOffByStorage() {
    try { return localStorage.getItem(STORE_KEY) === "1"; } catch (_) { return false; }
  }

  function getModeFromBody() {
    const v = document.body?.getAttribute("data-nova-cosmic-interactions") || "off";
    return v;
  }

  function enabled() {
    if (!document.body) return false;
    if (isOffByStorage()) return false;
    if (mqReduce().matches) return false;
    if (!mqFine().matches) return false;
    const mode = getModeFromBody();
    return mode !== "off";
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // UTILITIES
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // CORE CONTROLLER
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  function init(root = document) {
    if (!enabled()) return () => {};

    const ac = new AbortController();
    const { signal } = ac;

    let activeEl = null;
    let activeRect = null;
    let raf = 0;
    let lastX = 0;
    let lastY = 0;

    // Pick target element on pointer event
    function pickTarget(e) {
      const t = e.target;
      if (!t) return null;
      return t.closest?.(`${SELECT_MAGNET}, ${SELECT_TILT}`) || null;
    }

    // On pointer enter: cache bounding box
    function onEnter(e) {
      const el = pickTarget(e);
      if (!el) return;
      activeEl = el;
      activeRect = el.getBoundingClientRect();
    }

    // On pointer leave: reset transforms
    function onLeave() {
      if (activeEl) {
        activeEl.style.removeProperty("--nova-mx");
        activeEl.style.removeProperty("--nova-my");
        activeEl.style.removeProperty("--nova-rx");
        activeEl.style.removeProperty("--nova-ry");
      }
      activeEl = null;
      activeRect = null;
    }

    // rAF batch: apply transforms
    function apply() {
      raf = 0;
      if (!activeEl || !activeRect) return;

      const cx = activeRect.left + activeRect.width / 2;
      const cy = activeRect.top + activeRect.height / 2;

      const dx = clamp((lastX - cx) / (activeRect.width / 2), -1, 1);
      const dy = clamp((lastY - cy) / (activeRect.height / 2), -1, 1);

      // Magnetic proximity (px offset)
      if (activeEl.matches(SELECT_MAGNET)) {
        const mag = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue("--nova-lux-magnet")
        ) || 0;
        activeEl.style.setProperty("--nova-mx", `${(dx * mag).toFixed(2)}px`);
        activeEl.style.setProperty("--nova-my", `${(dy * mag).toFixed(2)}px`);
      }

      // Card tilt (deg rotation)
      if (activeEl.matches(SELECT_TILT)) {
        const tilt = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue("--nova-lux-tilt")
        ) || 0;
        activeEl.style.setProperty("--nova-rx", `${(-dy * tilt).toFixed(3)}deg`);
        activeEl.style.setProperty("--nova-ry", `${(dx * tilt).toFixed(3)}deg`);
      }
    }

    // On pointer move: throttle with rAF
    function onMove(e) {
      if (!activeEl) return;
      lastX = e.clientX;
      lastY = e.clientY;
      if (!raf) raf = requestAnimationFrame(apply);
    }

    // On resize: update cached bounding box
    function onResize() {
      if (activeEl) activeRect = activeEl.getBoundingClientRect();
    }

    // Event listeners (delegated, passive)
    root.addEventListener("pointerover", onEnter, { passive: true, signal });
    root.addEventListener("pointerout", (e) => {
      if (!activeEl) return;
      const rt = e.relatedTarget;
      if (!rt || (rt !== activeEl && !activeEl.contains(rt))) onLeave();
    }, { passive: true, signal });

    window.addEventListener("pointermove", onMove, { passive: true, signal });
    window.addEventListener("resize", onResize, { passive: true, signal });

    // React to media query changes (disable if conditions change)
    const mqF = mqFine();
    const mqR = mqReduce();
    const recheck = () => { if (!enabled()) ac.abort(); };
    mqF.addEventListener?.("change", recheck, { signal });
    mqR.addEventListener?.("change", recheck, { signal });

    return () => ac.abort();
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // BOOTSTRAP: AUTO-MARK TARGETS
  // Automatically add data-attributes to existing Nova components
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  function bootstrap() {
    const mode = getModeFromBody();
    if (mode === "off") return () => {};

    // Mark buttons as magnetic
    document.querySelectorAll(".nova-button, .nova-button-primary, .nova-cta-button").forEach(el => {
      if (!el.hasAttribute("data-nova-magnetic")) {
        el.setAttribute("data-nova-magnetic", "1");
      }
    });

    // Mark cards as tilt
    document.querySelectorAll(".nova-vault-card, .nova-provenance-panel").forEach(el => {
      if (!el.hasAttribute("data-nova-tilt")) {
        el.setAttribute("data-nova-tilt", "1");
      }
    });

    return init(document);
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // LIFECYCLE MANAGEMENT
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  let cleanup = null;

  function start() {
    if (cleanup) return; // Already running
    cleanup = bootstrap();
  }

  function stop() {
    if (cleanup) {
      try { cleanup(); } catch (_) {}
      cleanup = null;
    }
  }

  // DOMContentLoaded: init if enabled
  if (document.readyState === 'loading') {
    document.addEventListener("DOMContentLoaded", () => {
      if (enabled()) start();
    }, { once: true });
  } else {
    if (enabled()) start();
  }

  // Theme Editor lifecycle (Shopify)
  document.addEventListener("shopify:section:load", () => {
    stop();
    if (enabled()) start();
  });

  document.addEventListener("shopify:section:unload", () => {
    stop();
    if (enabled()) start();
  });

  // NovaMotion integration (if available)
  if (window.NovaMotion && typeof window.NovaMotion.registerModule === "function") {
    window.NovaMotion.registerModule(
      "lux-kit",
      () => { if (enabled()) start(); },
      () => stop()
    );
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PUBLIC API (Debugging + Manual Control)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  if (typeof window !== "undefined") {
    window.NovaLuxKit = {
      __version: VERSION,
      start,
      stop,
      enabled,
      setLocalStorageOff: () => { try { localStorage.setItem(STORE_KEY, "1"); stop(); } catch (_) {} },
      clearLocalStorageOff: () => { try { localStorage.removeItem(STORE_KEY); if (enabled()) start(); } catch (_) {} }
    };
  }
})();
